import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { armDashboardTour } from '@features/Dashboard';
import {
  clearTrainingResult,
  finishOnboarding,
  OnboardingResult,
  onboardingErrorKind,
  readTrainingResult,
  reminderEventProps,
  resultFromNavigationState,
  useLeaveOnboardingResultMutation,
  useOnboardingStepViewed,
  type ReminderChoice,
} from '@features/Onboarding';
import { trackEvent } from '@shared/analytics';

/**
 * Итог знакомства. Знакомство засчитывается, как только ученик дошёл сюда (B5, 2026-10-20): закрыл
 * приложение на итоге — при следующем входе главная, а не знакомство с начала. «На главную» меняет
 * стадию на app — OnboardingGate уводит на главную; выбора времени напоминаний нет (решение владельца
 * 2026-10-21), уходит null — 19:00 МСК. Почту здесь не просим (2026-10-28) — см. ниже.
 */
export default function OnboardingResultPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const leave = useLeaveOnboardingResultMutation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  // Ранний finish-onboarding без полей: стадию не меняет (время ещё выбирают), один запрос и в
  // StrictMode (ref). true — знакомство на сервере завершено, этим запросом или раньше.
  const earlyFinish = useRef<Promise<boolean> | null>(null);
  // Состояние навигации главнее: «выйти» из тренировки — явный null, даже если в хранилище
  // остался итог прошлой попытки; без состояния (адрес открыт заново) — итог из sessionStorage.
  const [result] = useState(() => {
    const fromState = resultFromNavigationState(location.state);
    return fromState !== undefined ? fromState : readTrainingResult();
  });
  useOnboardingStepViewed('result');

  useEffect(() => {
    if (earlyFinish.current) return;
    // Подсветку главной включаем и здесь: закрыл приложение на итоге — она покажется при следующем входе.
    armDashboardTour();
    earlyFinish.current = finishOnboarding({ module_ids: [] }).then(
      () => true,
      () => false,
    );
  }, []);

  const handleFinish = async (choice: ReminderChoice) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    armDashboardTour();
    try {
      const finishedEarly = (await earlyFinish.current) ?? false;
      await leave.mutateAsync({ choice, finishedEarly });
      const props = reminderEventProps(choice);
      if (props) trackEvent('reminder_time_selected', props);
      clearTrainingResult();
    } catch (err) {
      busyRef.current = false;
      setBusy(false);
      setError(onboardingErrorKind(err) === 'network' ? t('onboarding.intro.networkError') : t('onboarding.errorRetry'));
    }
  };

  // Почту здесь не просим (решение владельца 2026-10-28): с итога знакомства ученица ушла в почтовый ящик за письмом и
  // не вернулась. Главная кнопка — дальше в приложение; подтвердить почту позовёт плашка главной и итог миссии.
  return <OnboardingResult result={result} emailSlot={null} busy={busy} error={error} onFinish={handleFinish} />;
}
